import { useState } from 'react';
import { Plus, Minus } from 'lucide-react';
import { cn } from '../../lib/cn';

const FAQS = [
  {
    q: 'What pharmacy management systems do you support?',
    a: 'Any PMS that exports a delivery manifest as CSV. PioneerRx, QS/1, Liberty, Computer-Rx, BestRx, and others. Direct PMS API integrations are on the roadmap, prioritized by customer demand.',
  },
  {
    q: 'Are you HIPAA compliant?',
    a: 'Yes. LYN Rx infrastructure is HIPAA-compliant, and we sign a Business Associate Agreement with every customer. Our infrastructure providers (Supabase, Vercel, Twilio Enterprise) all have BAAs in place.',
  },
  {
    q: 'How long does implementation take?',
    a: 'First customer signed in July 2026. Typical onboarding for a single-location pharmacy is 1–2 days. Multi-location implementations: 3–5 days. Hospital outpatient pharmacies with Epic Willow: 2–4 weeks due to IT coordination.',
  },
  {
    q: 'What if my drivers aren\'t tech-savvy?',
    a: 'The driver app is designed for one-handed use during a delivery shift. The longest workflow (POD capture) is six steps. Drivers we\'ve onboarded have averaged 15 minutes from first install to first successful delivery.',
  },
  {
    q: 'What happens to my data if we cancel?',
    a: 'You can export all your data on request — audit logs, POD records, photos, signatures. Data is retained for 30 days post-cancellation, then permanently deleted with confirmation.',
  },
  {
    q: 'Do you charge per delivery or per pharmacy?',
    a: 'Per-tenant subscription based on combined monthly delivery volume across all your pharmacies. One operation = one subscription.',
  },
  {
    q: 'What about patient SMS?',
    a: 'Optional. Patients must explicitly opt in. SMS sends use Twilio\'s HIPAA-compliant Enterprise tier.',
  },
  {
    q: 'How do I get started?',
    a: 'Request a demo. We\'ll walk through your specific pharmacy operation and answer questions. If LYN Rx is a fit, onboarding starts within a week.',
  },
] as const;

export function Faq() {
  const [open, setOpen] = useState<number | null>(0);
  return (
    <section id="faq" className="bg-white py-24 md:py-32">
      <div className="container-page">
        <div className="mx-auto max-w-3xl">
          <div className="text-center">
            <p className="text-xs font-medium uppercase tracking-wider text-primary">FAQ</p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight text-ink md:text-4xl">
              Questions pharmacy directors actually ask.
            </h2>
          </div>

          <div className="mt-12 divide-y divide-border rounded-2xl border border-border bg-white shadow-sm">
            {FAQS.map((item, i) => {
              const isOpen = open === i;
              return (
                <div key={item.q}>
                  <button
                    type="button"
                    onClick={() => setOpen(isOpen ? null : i)}
                    aria-expanded={isOpen}
                    className="flex w-full items-center justify-between gap-6 px-6 py-5 text-left text-sm font-medium text-ink transition-colors hover:bg-bg-soft"
                  >
                    <span>{item.q}</span>
                    <span className="flex h-6 w-6 flex-none items-center justify-center rounded-full border border-border text-ink-2">
                      {isOpen ? <Minus className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
                    </span>
                  </button>
                  <div
                    className={cn(
                      'grid overflow-hidden transition-all duration-300',
                      isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0',
                    )}
                  >
                    <div className="min-h-0">
                      <p className="px-6 pb-5 text-sm leading-relaxed text-ink-2">{item.a}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
